import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, ChevronLeft, UserRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "../lib/perms";

const OFFER_STATUSES = ["Pending","Offer Issued","Offer Accepted","Offer Denied"];
const offerBadgeCls = (s) => ({
  "Pending": "bg-slate-50 text-slate-700 border-slate-300",
  "Offer Issued": "bg-blue-50 text-blue-900 border-blue-300",
  "Offer Accepted": "bg-emerald-50 text-emerald-900 border-emerald-300",
  "Offer Denied": "bg-rose-50 text-rose-900 border-rose-300",
}[s || "Pending"]);

export default function B2CStudentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [openApp, setOpenApp] = useState(false);
  const [f, setF] = useState({});

  const load = async () => {
    const [d, m] = await Promise.all([api.get(`/b2c/students/${id}`), api.get("/b2c/meta")]);
    setData(d.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  // Compute accepted application (prefer server-provided; else compute client-side)
  const accepted = useMemo(() => {
    if (!data || !meta) return null;
    if (data.accepted_application) return data.accepted_application;
    const a = (data.applications || []).find(x => x.offer_status === "Offer Accepted");
    if (!a) return null;
    return {
      application: a,
      college: meta.colleges.find(c => c.id === a.college_id) || null,
      program: meta.programs.find(p => p.id === a.program_id) || null,
      intake:  meta.intakes.find(i => i.id === a.intake_id)  || null,
    };
  }, [data, meta]);

  if (!data || !meta) return <div>Loading…</div>;
  const s = data.student;

  const patch = async (body) => { await api.patch(`/b2c/students/${id}`, body); load(); };

  const updateOfferStatus = async (appId, value) => {
    try {
      await api.patch(`/b2c/applications/${appId}`, { offer_status: value });
      toast.success(`Offer status → ${value}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createApp = async () => {
    if (!f.college_id || !f.program_category || !f.intake_id) return toast.error("College, Program Category & Intake are required");
    try {
      const r = await api.post("/b2c/applications", { ...f, student_id: id });
      toast.success(`Created ${r.data.application_id}`);
      setOpenApp(false); setF({});
      nav(`/b2c/applications/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const officers = meta.users.filter(u => ["admissions_officer","admin","manager"].includes(u.role));
  const counsellors = meta.users.filter(u => u.role === "admissions_officer" && u.is_active !== false);
  const canEditCounsellor = ["admin","manager"].includes(me?.role || "");
  const currentCounsellor = meta.users.find(u => u.user_id === s.counsellor_id);

  const acceptedLine = accepted
    ? [accepted.college?.name, accepted.program?.name, accepted.intake?.name].filter(Boolean).join(" · ")
    : null;

  return (
    <div className="space-y-6">
      <Link to="/b2c/students" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4" /> Back to students</Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center"><UserRound className="w-6 h-6" /></div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="heading-font text-2xl font-bold text-slate-900">{s.full_name}</h1>
              <span className="mono text-xs text-slate-500">{s.student_id}</span>
            </div>
            {/* Dynamic accepted college + program + intake line */}
            {acceptedLine ? (
              <div data-testid="student-accepted-line" className="text-sm font-medium text-emerald-800 mt-1 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>{acceptedLine}</span>
                <span className="stage-badge bg-emerald-50 text-emerald-900 border-emerald-300 ml-1">Offer Accepted</span>
              </div>
            ) : (
              <div data-testid="student-accepted-line" className="text-sm text-slate-400 mt-1 italic">No offer accepted yet</div>
            )}
            <div className="text-sm text-slate-500 mt-1">{s.email} · {s.phone}</div>
          </div>
          <Button data-testid="new-app-btn" onClick={()=>setOpenApp(true)} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2"/>New Application</Button>
        </div>
      </div>

      <Tabs defaultValue="applications">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications">Applications ({data.applications.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Full Name"><Input defaultValue={s.full_name||""} onBlur={e=>e.target.value!==s.full_name && patch({full_name: e.target.value})}/></F>
            <F label="Email"><Input defaultValue={s.email||""} onBlur={e=>e.target.value!==s.email && patch({email: e.target.value})}/></F>
            <F label="Phone"><Input defaultValue={s.phone||""} onBlur={e=>e.target.value!==s.phone && patch({phone: e.target.value})}/></F>
            <F label="UCI (Unique Client ID)"><Input defaultValue={s.uci||""} onBlur={e=>e.target.value!==(s.uci||"") && patch({uci: e.target.value})}/></F>
            <F label="City"><Input defaultValue={s.city||""} onBlur={e=>e.target.value!==s.city && patch({city: e.target.value})}/></F>
            <F label="Country"><Input defaultValue={s.country||""} onBlur={e=>e.target.value!==s.country && patch({country: e.target.value})}/></F>
            <F label="Counsellor (locked to Admin/Manager)">
              {canEditCounsellor ? (
                <Select value={s.counsellor_id || ""} onValueChange={v=>patch({counsellor_id: v})}>
                  <SelectTrigger data-testid="counsellor-select"><SelectValue placeholder="Assign a counsellor"/></SelectTrigger>
                  <SelectContent>
                    {counsellors.length === 0 && <SelectItem value="__none__" disabled>No admissions officers available — add users with role "admissions_officer"</SelectItem>}
                    {counsellors.map(u => <SelectItem key={u.user_id} value={u.user_id} data-testid={`counsellor-opt-${u.user_id}`}>{u.name} · {u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div data-testid="counsellor-readonly" className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700 flex items-center">
                  {currentCounsellor ? `${currentCounsellor.name} · ${currentCounsellor.email}` : <span className="text-slate-400 italic">Unassigned</span>}
                  <span className="ml-auto text-xs text-slate-500">🔒 Admin/Manager only</span>
                </div>
              )}
            </F>
            <div className="md:col-span-2"><F label="Notes"><Textarea defaultValue={s.notes||""} onBlur={e=>e.target.value!==s.notes && patch({notes: e.target.value})}/></F></div>
          </div>
        </TabsContent>
        <TabsContent value="applications">
          {/* Current Accepted Application summary */}
          <div data-testid="accepted-app-summary" className={`rounded-xl border p-4 mb-3 ${accepted ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-600">Current Accepted Application</div>
            {accepted ? (
              <div className="mt-1 text-sm text-slate-900">
                <div className="font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600"/>{accepted.college?.name || "—"}</div>
                <div className="text-slate-700">Course: <span className="font-medium">{accepted.program?.name || "—"}</span></div>
                <div className="text-slate-700">Intake: <span className="font-medium">{accepted.intake?.name || "—"}</span></div>
                <div className="mt-1"><span className="stage-badge bg-emerald-100 text-emerald-900 border-emerald-300">Offer Accepted</span></div>
              </div>
            ) : (
              <div className="mt-1 text-sm text-slate-500 italic">Not Selected — mark one application below as "Offer Accepted".</div>
            )}
          </div>

          <div className="space-y-3">
            {data.applications.map((a, idx) => {
              const c = meta.colleges.find(c=>c.id===a.college_id);
              const p = meta.programs.find(p=>p.id===a.program_id) || (a.course_name ? { name: a.course_name } : null);
              const i = meta.intakes.find(i=>i.id===a.intake_id);
              const offer = a.offer_status || "Pending";
              return (
                <div key={a.id} data-testid={`app-card-${a.application_id}`}
                     className={`w-full bg-white p-5 rounded-xl border transition-shadow hover:shadow-md ${offer === "Offer Accepted" ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <button onClick={()=>nav(`/b2c/applications/${a.id}`)} className="text-left flex-1 min-w-0">
                      <div className="mono text-xs text-slate-500">College {idx+1} · {a.application_id}</div>
                      <div className="heading-font font-semibold text-slate-900 mt-1">{c?.name || "—"}</div>
                      <div className="text-sm text-slate-600">{p?.name || "—"} · {i?.name || "—"}</div>
                    </button>
                    <div className="flex flex-col items-end gap-2 min-w-[220px]">
                      <div className="flex items-center gap-2 w-full justify-end">
                        <Label className="text-xs text-slate-500 whitespace-nowrap">Offer Status</Label>
                        <Select value={offer} onValueChange={(v)=>updateOfferStatus(a.id, v)}>
                          <SelectTrigger data-testid={`offer-status-${a.application_id}`} className="h-8 w-40">
                            <SelectValue/>
                          </SelectTrigger>
                          <SelectContent>
                            {OFFER_STATUSES.map(o => (
                              <SelectItem key={o} value={o} data-testid={`offer-opt-${a.application_id}-${o.replace(/\s+/g,"-")}`}>
                                {o}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        <span className={`stage-badge ${offerBadgeCls(offer)}`}>{offer}</span>
                        <span className="stage-badge bg-blue-50 text-blue-900 border-blue-300">{a.stage}{a.stage === "Deferred" && a.deferred_to ? ` → ${a.deferred_to}` : ""}</span>
                        {a.filing_started && <span className="stage-badge bg-purple-50 text-purple-800 border-purple-300">Filing: {a.filing_status}</span>}
                        {a.pal_pending && <span className="stage-badge bg-amber-50 text-amber-900 border-amber-300">PAL Pending</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {data.applications.length===0 && <div className="bg-white p-8 rounded-xl border border-slate-200 text-center text-slate-500">No applications yet.</div>}
          </div>
        </TabsContent>
        <TabsContent value="documents">
          <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">Documents are managed on each application.</div>
        </TabsContent>
        <TabsContent value="activity">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <ol className="relative border-l border-slate-200 space-y-3 pl-4">
              {data.activity.map(ev => (
                <li key={ev.id}>
                  <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-orange-500" />
                  <div className="text-xs text-slate-500">{new Date(ev.created_at).toLocaleString()} · {ev.actor_name}</div>
                  <div className="text-sm">{ev.action.replace(/_/g," ")}</div>
                </li>
              ))}
              {data.activity.length===0 && <li className="text-slate-500 text-sm">No activity yet.</li>}
            </ol>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={openApp} onOpenChange={setOpenApp}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>New Application for {s.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>College *</Label>
              <Select value={f.college_id||""} onValueChange={v=>setF({...f, college_id:v})}>
                <SelectTrigger data-testid="app-college-select"><SelectValue placeholder="Choose"/></SelectTrigger>
                <SelectContent>{meta.colleges.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Program Category *</Label>
              <Select value={f.program_category||""} onValueChange={v=>setF({...f, program_category:v})}>
                <SelectTrigger data-testid="app-category-select"><SelectValue placeholder="Choose category"/></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Course Name</Label>
              <Input data-testid="app-course-input" value={f.course_name||""} onChange={e=>setF({...f, course_name:e.target.value})} placeholder="e.g. Master of Data Analytics" />
            </div>
            <div><Label>Intake *</Label>
              <Select value={f.intake_id||""} onValueChange={v=>setF({...f, intake_id:v})}>
                <SelectTrigger data-testid="app-intake-select"><SelectValue placeholder="Choose"/></SelectTrigger>
                <SelectContent>{meta.intakes.map(i=><SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Assigned Admission Officer</Label>
              <Select value={f.admission_officer_id||""} onValueChange={v=>setF({...f, admission_officer_id:v})}>
                <SelectTrigger><SelectValue placeholder="Choose"/></SelectTrigger>
                <SelectContent>{officers.map(u=><SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpenApp(false)}>Cancel</Button><Button data-testid="new-app-submit" onClick={createApp} className="bg-orange-500 hover:bg-orange-600 text-white">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({label, children}) => (<div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>);
